// ================================================================
// Q&A 分析 API 模組
// ================================================================
// 版本：1.0
// 日期：2026-01-14
// 功能：執行問答分析、餘額扣款、查詢歷史、交易記錄
// ================================================================

import apiClient from './client';
import { Question } from './questions';

// ================================================================
// 型別定義
// ================================================================

/**
 * Q&A 分析請求
 */
export interface QARequest {
  question_id: string;        // 問題ID
  birth_time: string;         // 出生時間 YYYYMMDDhh
  gender: 'Male' | 'Female';  // 性別
}

/**
 * Q&A 分析響應
 */
export interface QAResponse {
  success: boolean;
  question_id: string;
  question_text: string;
  birth_time: string;
  gender: 'Male' | 'Female';
  output: string;             // 分析輸出文字
  result: any;                // 分析結果資料
  monthly_free_used?: boolean; // 是否使用了本月免費問題
  error?: string;
  message?: string;
}

export interface MonthlyFreeStatusResponse {
  success: boolean;
  monthly_free_available: boolean;
  reset_at?: string | null;  // 下次重置時間（若本月已用）
}

/**
 * 查詢歷史記錄
 */
export interface QueryHistory {
  query_id: number;
  user_id: number;
  question_id: string;
  question_text: string;
  birth_time: string;
  gender: 'Male' | 'Female';
  answer_text: string;  // 修正：資料庫欄位為 answer_text，而非 result_text
  cost: number;  // 修正：資料庫欄位為 cost，而非 price_paid
  currency: string;
  created_at: string;
}

/**
 * 查詢歷史響應
 */
export interface QueryHistoryResponse {
  success: boolean;
  count: number;
  queries: QueryHistory[];
  error?: string;
}

/**
 * 交易記錄
 */
export interface Transaction {
  transaction_id: number;
  user_id: number;
  transaction_type: string;   // 'register_bonus', 'query_charge', 'refund', etc.
  amount: number;
  currency: string;
  description: string;
  balance_after: number;
  created_at: string;
}

/**
 * 交易記錄響應
 */
export interface TransactionHistoryResponse {
  success: boolean;
  count: number;
  transactions: Transaction[];
  error?: string;
}

// ================================================================
// API 函數
// ================================================================

/**
 * 執行 Q&A 分析（扣款）
 *
 * @param request - Q&A 請求參數
 * @returns 分析結果
 *
 * @example
 * const result = await askQuestion({
 *   question_id: 'MAIN001',
 *   birth_time: '1995032103',
 *   gender: 'Male'
 * });
 * console.log(result.output);
 */
export const askQuestion = async (request: QARequest): Promise<QAResponse> => {
  try {
    const response = await apiClient.post<QAResponse>('/api/qa/ask', request);
    return response as any as QAResponse;
  } catch (error: any) {
    if (error.response?.status === 402) {
      throw new Error('餘額不足，請先充值');
    }
    if (error.response?.status === 404) {
      throw new Error('問題不存在');
    }
    if (error.response?.status === 501) {
      throw new Error('配對分析功能尚未開放，敬請期待');
    }
    throw new Error(error.response?.data?.error || 'Q&A 分析失敗');
  }
};

/**
 * 取得查詢歷史
 *
 * @param limit - 限制數量（選填）
 * @param offset - 偏移量（選填）
 * @returns 查詢歷史列表
 *
 * @example
 * const history = await getQueryHistory(20, 0);
 */
export const getQueryHistory = async (
  limit?: number,
  offset?: number
): Promise<QueryHistoryResponse> => {
  try {
    const params: any = {};

    if (limit !== undefined) {
      params.limit = limit;
    }

    if (offset !== undefined) {
      params.offset = offset;
    }

    const response = await apiClient.get<QueryHistoryResponse>('/api/user/queries', { params });
    return response as any as QueryHistoryResponse;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || '查詢歷史載入失敗');
  }
};

/**
 * 取得交易記錄
 *
 * @param limit - 限制數量（選填）
 * @param offset - 偏移量（選填）
 * @returns 交易記錄列表
 *
 * @example
 * const transactions = await getTransactionHistory(20, 0);
 */
export const getTransactionHistory = async (
  limit?: number,
  offset?: number
): Promise<TransactionHistoryResponse> => {
  try {
    const params: any = {};

    if (limit !== undefined) {
      params.limit = limit;
    }

    if (offset !== undefined) {
      params.offset = offset;
    }

    const response = await apiClient.get<TransactionHistoryResponse>('/api/user/transactions', { params });
    return response as any as TransactionHistoryResponse;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || '交易記錄載入失敗');
  }
};

/**
 * 取得特定查詢詳情
 *
 * @param query_id - 查詢ID
 * @returns 查詢詳情
 */
export const getQueryDetail = async (query_id: number): Promise<QueryHistory> => {
  try {
    const response = await apiClient.get<{ success: boolean; query: QueryHistory }>(
      `/api/user/queries/${query_id}`
    );
    return (response as any).query;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('查詢記錄不存在');
    }
    throw new Error(error.response?.data?.error || '查詢詳情載入失敗');
  }
};

// ================================================================
// 輔助函數
// ================================================================

/**
 * 格式化交易類型
 */
export const formatTransactionType = (type: string): string => {
  const types: Record<string, string> = {
    'register_bonus': '註冊贈送',
    'query_charge': '查詢扣款',
    'refund': '退款',
    'recharge': '充值',
    'admin_adjustment': '管理員調整'
  };
  return types[type] || type;
};

/**
 * 格式化金額顯示
 */
export const formatAmount = (amount: number, currency: string = 'NTD'): string => {
  if (amount >= 0) {
    return `+${currency} ${amount}`;
  } else {
    return `${currency} ${amount}`;
  }
};

/**
 * 格式化日期時間
 */
export const formatDateTime = (datetime: string): string => {
  try {
    const date = new Date(datetime);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return datetime;
  }
};

/**
 * 計算查詢成本
 */
export const calculateQueryCost = (question: Question): number => {
  return question.price || 0;
};

/**
 * 檢查餘額是否足夠
 * 規則：
 * 1. 如果餘額 >= cost，直接允許
 * 2. 如果餘額不足但扣款後不超過透支上限，也允許
 * 3. 透支上限為 NTD 500
 */
export const checkBalanceSufficient = (balance: number, cost: number): boolean => {
  // 正餘額直接檢查是否足夠支付
  if (balance >= cost) {
    return true;
  }
  
  // 允許欠款上限 NTD 500
  const DEBT_LIMIT = 500;
  // 檢查扣款後是否會超過透支上限
  return (balance - cost) >= -DEBT_LIMIT;
};

/**
 * 取得餘額狀態
 */
export const getBalanceStatus = (balance: number): {
  status: 'good' | 'warning' | 'critical';
  message: string;
  color: string;
} => {
  if (balance >= 100) {
    return {
      status: 'good',
      message: '餘額充足',
      color: '#4caf50'
    };
  } else if (balance >= 0) {
    return {
      status: 'warning',
      message: '餘額偏低，建議充值',
      color: '#ff9800'
    };
  } else if (balance > -500) {
    return {
      status: 'critical',
      message: `已使用透支額度 NTD ${Math.abs(balance)}`,
      color: '#f44336'
    };
  } else {
    return {
      status: 'critical',
      message: '欠款已超過 NTD 500，請加值後本系統再繼續提供服務',
      color: '#d32f2f'
    };
  }
};

/**
 * 命盤特色摘要回應
 */
export interface ChartSummaryResponse {
  success: boolean;
  main_stars?: string[];
  display_star?: string;
  description?: string;
  tip?: string;
  mingon_position?: number;
  error?: string;
}

/**
 * 取得命盤特色摘要（命宮主星與特色描述）
 */
export const getChartSummary = async (): Promise<ChartSummaryResponse> => {
  try {
    const response = await apiClient.get<ChartSummaryResponse>('/api/user/chart-summary') as any as ChartSummaryResponse;
    return response;
  } catch (error: any) {
    return { success: false };
  }
};

/**
 * 取得本月免費問題狀態
 */
export const getMonthlyFreeStatus = async (): Promise<MonthlyFreeStatusResponse> => {
  try {
    const response = await apiClient.get<MonthlyFreeStatusResponse>('/api/user/monthly-free') as any as MonthlyFreeStatusResponse;
    return response;
  } catch (error: any) {
    return { success: false, monthly_free_available: false };
  }
};
